import { useState } from 'react';
import { Button, Card, Form, Input, Typography } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { useGetCsrfToken } from '@/shared/hooks/useGetCsrfToken';
import { useMessage } from '@/shared/hooks/useMessage';
import { useAuthUser } from '@/modules/auth/context/AuthContext';
import { useTheme } from '@/components/common/ThemeProvider';

const { Title } = Typography;

interface ChangePasswordViewProps {
    userId?: string;
    onSuccess?: () => void;
}

const ChangePasswordView: React.FC<ChangePasswordViewProps> = ({ userId: propUserId, onSuccess }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const message = useMessage();
    const csrfToken = useGetCsrfToken();
    const authUser = useAuthUser();
    const { dark } = useTheme();
    
    // Use prop userId if provided, otherwise use authUser.userId
    const userId = propUserId || authUser.userId;

    const handleSubmit = async (values: any) => {
        try {
            setLoading(true);
            const response = await fetch(`/api/nub/users/${userId}/password`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': csrfToken
                },
                body: JSON.stringify({
                    currentPassword: values.currentPassword,
                    newPassword: values.newPassword
                })
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || 'Failed to change password');
            }

            message.success('Password changed successfully. Please log in with your new password.');
            form.resetFields();

            if (onSuccess) {
                onSuccess();
            }

            // Use the logout function from useAuth hook
            setTimeout(() => {
                // Ensure no stale persisted auth user remains (e.g., changePassword=true)
                // so that after re-login the app doesn't redirect back here incorrectly.
                localStorage.removeItem('auth-storage');
                window.location.href = '/login';
            }, 2000);

        } catch (error: any) {
            message.error(error.message || 'Failed to change password');
        } finally {
            setLoading(false);
        }
    };

    const validateNewPassword = (_: any, value: string) => {
        const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

        if (!value) {
            return Promise.reject('Please input your new password!');
        }

        if (!passwordRegex.test(value)) {
            return Promise.reject(
                'Password must be at least 8 characters and include uppercase, lowercase, number and special character'
            );
        }

        return Promise.resolve();
    };

    return (
        <div className={`p-4 md:p-6 ${dark ? 'bg-gray-900 text-gray-100' : 'bg-gray-100 text-gray-800'}`}>
            <Card className={dark ? 'bg-gray-800' : ''}>
                <Title level={2} className={dark ? 'text-gray-100' : ''}>Change Password</Title>
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                >
                <Form.Item
                    name="currentPassword"
                    label="Current Password"
                    rules={[{ required: true, message: 'Please input your current password!' }]}
                >
                    <Input.Password
                        prefix={<LockOutlined />}
                        placeholder="Enter current password"
                    />
                </Form.Item>

                <Form.Item
                    name="newPassword"
                    label="New Password"
                    rules={[{ validator: validateNewPassword }]}
                    extra="Password must be at least 8 characters and include uppercase, lowercase, number and special character"
                >
                    <Input.Password
                        prefix={<LockOutlined />}
                        placeholder="Enter new password"
                    />
                </Form.Item>

                <Form.Item
                    name="confirmPassword"
                    label="Confirm New Password"
                    dependencies={['newPassword']}
                    rules={[
                        { required: true, message: 'Please confirm your new password!' },
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                if (!value || getFieldValue('newPassword') === value) {
                                    return Promise.resolve();
                                }
                                return Promise.reject('The two passwords do not match!');
                            },
                        }),
                    ]}
                >
                    <Input.Password
                        prefix={<LockOutlined />}
                        placeholder="Confirm new password"
                    />
                </Form.Item>

                <Form.Item>
                    <Button type="primary" htmlType="submit" loading={loading}>
                        Change Password
                    </Button>
                </Form.Item>
            </Form>
        </Card>
        </div>
    );
};

export default ChangePasswordView;